import { ValidationResponse, InstructionsResponse, SampleDataResponse, SAPCategoriesResponse, SAPMainCategory, SAPSubCategory } from '../types';
import { API_BASE_URL, API_ENDPOINTS, EDGE_FUNCTIONS_URL, EDGE_ENDPOINTS } from '../constants/api';

// Helper to get auth + org + schema headers (used by all protected endpoints)
const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('auth_token');
  const orgId = localStorage.getItem('active_org_id');
  const schema = localStorage.getItem('active_schema');
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (orgId) headers['X-Organization-Id'] = orgId;
  if (schema) headers['X-Schema'] = schema;
  return headers;
};

export const api = {
  // ─── Instructions ───────────────────────────────────────────────
  getInstructions: async (category: string): Promise<InstructionsResponse> => {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.CATEGORY.INSTRUCTIONS(category)}`, {
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
    });
    if (!response.ok) throw new Error('Failed to fetch instructions');
    return response.json();
  },

  getInstructionsBySubcategory: async (subcategoryName: string): Promise<InstructionsResponse> => {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.CATEGORY.INSTRUCTIONS_BY_SUBCATEGORY(encodeURIComponent(subcategoryName))}`, {
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
    });
    if (!response.ok) throw new Error(`Failed to fetch instructions for ${subcategoryName}`);
    return response.json();
  },

  updateFieldInstructionRemark: async (subcategoryName: string, sapFieldName: string, remarks: string): Promise<{ success: boolean; message?: string }> => {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.CATEGORY.REMARK(encodeURIComponent(subcategoryName))}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ sap_field_name: sapFieldName, remarks }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Failed to update remarks');
    return data;
  },

  getFieldVisibility: async (subcategoryName: string): Promise<{ success: boolean; visibility?: Record<string, boolean> }> => {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.CATEGORY.VISIBILITY(encodeURIComponent(subcategoryName))}`, {
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Failed to fetch visibility');
    return data;
  },

  updateFieldVisibility: async (
    subcategoryName: string,
    payload: { fieldKey?: string; showInValidation?: boolean; updates?: Array<{ fieldKey: string; showInValidation: boolean }> }
  ): Promise<{ success: boolean; message?: string }> => {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.CATEGORY.VISIBILITY(encodeURIComponent(subcategoryName))}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Failed to update visibility');
    return data;
  },

  getDynamicFieldInstructions: async (subcategoryName: string): Promise<InstructionsResponse> => {
    const cacheBuster = `?t=${Date.now()}`;
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.CATEGORY.INSTRUCTIONS_BY_SUBCATEGORY(encodeURIComponent(subcategoryName))}${cacheBuster}`, {
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
    });
    if (!response.ok) throw new Error(`Failed to fetch dynamic field instructions for ${subcategoryName}`);
    return response.json();
  },

  // ─── Validation (synchronous — direct OpenAI call, no polling) ─
  // Submit data + get results back directly in one call
  submitValidation: async (_category: string, data: any[], _headers: string[] = [], subcategory?: string): Promise<{ results: any[]; tokensUsed: number; totalRows: number }> => {
    const response = await fetch(`${EDGE_FUNCTIONS_URL}${EDGE_ENDPOINTS.VALIDATE.SUBMIT}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ data, subcategory: subcategory || _category }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const err: any = new Error(errorData.message || 'Validation failed');
      err.code = errorData.data?.code || errorData.code;
      err.status = response.status;
      throw err;
    }
    const result = await response.json();
    return result.data;
  },

  // Legacy wrapper for backward compatibility
  validateData: async (category: string, data: any[], headers: string[] = [], subcategory?: string): Promise<ValidationResponse> => {
    const result = await api.submitValidation(category, data, headers, subcategory);
    return {
      success: true,
      category,
      results: result.results || [],
      summary: { total: result.totalRows, valid: Math.max(0, result.totalRows - (result.results?.length || 0)), warnings: 0, errors: result.results?.length || 0 },
      headers,
    };
  },

  // ─── Sample Data ───────────────────────────────────────────────
  getSampleData: async (category: string): Promise<SampleDataResponse> => {
    const response = await fetch(`${API_BASE_URL}/download-sample/${category}`, {
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
    });
    if (!response.ok) throw new Error('Failed to fetch sample data');
    return response.json();
  },

  // ─── Field Instructions CRUD ──────────────────────────────────
  createFieldInstruction: async (category: string, instructionData: any): Promise<any> => {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.INSTRUCTIONS.CREATE(category)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(instructionData),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to create field instruction');
    }
    return response.json();
  },

  // ─── SAP Categories ───────────────────────────────────────────
  getSAPCategories: async (): Promise<SAPCategoriesResponse> => {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.CATEGORIES.ALL}`, {
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
    });
    if (!response.ok) throw new Error(`Failed to fetch SAP categories: ${response.status} ${response.statusText}`);
    return response.json();
  },

  getMainCategories: async (): Promise<{ success: boolean; data: SAPMainCategory[] }> => {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.CATEGORIES.MAIN}`, {
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
    });
    if (!response.ok) throw new Error('Failed to fetch main categories');
    return response.json();
  },

  getSubcategoriesByMainCategoryId: async (mainCategoryId: number): Promise<{ success: boolean; data: SAPSubCategory[] }> => {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.CATEGORIES.SUBCATEGORIES(mainCategoryId)}`, {
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
    });
    if (!response.ok) throw new Error('Failed to fetch subcategories');
    return response.json();
  },

  getSubcategoriesByMainCategoryName: async (mainCategoryName: string): Promise<{ success: boolean; data: SAPSubCategory[] }> => {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.CATEGORIES.SUBCATEGORIES_BY_NAME(encodeURIComponent(mainCategoryName))}`, {
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
    });
    if (!response.ok) throw new Error('Failed to fetch subcategories');
    return response.json();
  },

  downloadFile: async (subCategoryId: number, fileType: 'template' | 'sample'): Promise<Blob> => {
    const response = await fetch(`${API_BASE_URL}/categories/download/${subCategoryId}/${fileType}`, {
      headers: { ...authHeaders() },
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to download file');
    }
    return response.blob();
  },

  // ─── Dynamic Data ─────────────────────────────────────────────
  getDataByTableName: async (tableName: string, params?: {
    page?: number; limit?: number; search?: string; sortBy?: string; sortOrder?: 'ASC' | 'DESC';
  }): Promise<any> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    const url = `${API_BASE_URL}/dynamic-data/table/${encodeURIComponent(tableName)}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch data');
    }
    return response.json();
  },

  // ─── Payments (via payments Edge Function) ─────────────────────
  getPlans: async () => {
    const response = await fetch(`${EDGE_FUNCTIONS_URL}${EDGE_ENDPOINTS.PAYMENTS.PLANS}`, {
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
    });
    if (!response.ok) throw new Error('Failed to fetch plans');
    return response.json();
  },

  createPaymentIntent: async (planId: string) => {
    const response = await fetch(`${EDGE_FUNCTIONS_URL}${EDGE_ENDPOINTS.PAYMENTS.CREATE_INTENT}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ planId }),
    });
    if (!response.ok) throw new Error('Failed to create payment intent');
    return response.json();
  },

  createSubscription: async (planId: string) => {
    const response = await fetch(`${EDGE_FUNCTIONS_URL}${EDGE_ENDPOINTS.PAYMENTS.CREATE_SUBSCRIPTION}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ planId }),
    });
    if (!response.ok) throw new Error('Failed to create subscription');
    return response.json();
  },

  confirmPayment: async (planId: string, subscriptionId?: string) => {
    const response = await fetch(`${EDGE_FUNCTIONS_URL}${EDGE_ENDPOINTS.PAYMENTS.CONFIRM}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ planId, subscriptionId }),
    });
    if (!response.ok) throw new Error('Failed to confirm payment');
    return response.json();
  },

  getMyPlan: async () => {
    const response = await fetch(`${EDGE_FUNCTIONS_URL}${EDGE_ENDPOINTS.PAYMENTS.MY_PLAN}`, {
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
    });
    if (!response.ok) throw new Error('Failed to get plan');
    return response.json();
  },

  getUsageHistory: async () => {
    const response = await fetch(`${EDGE_FUNCTIONS_URL}${EDGE_ENDPOINTS.PAYMENTS.USAGE_HISTORY}`, {
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
    });
    if (!response.ok) throw new Error('Failed to get usage history');
    return response.json();
  },

  getAdminUsage: async () => {
    const response = await fetch(`${EDGE_FUNCTIONS_URL}${EDGE_ENDPOINTS.PAYMENTS.ADMIN_USAGE}`, {
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
    });
    if (!response.ok) throw new Error('Failed to get admin usage');
    return response.json();
  },

  // ─── File Upload (via upload Edge Function) ────────────────────
  uploadSchema: async (formData: FormData) => {
    const token = localStorage.getItem('auth_token');
    const orgId = localStorage.getItem('active_org_id');
    const schema = localStorage.getItem('active_schema');
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (orgId) headers['X-Organization-Id'] = orgId;
    if (schema) headers['X-Schema'] = schema;
    // Don't set Content-Type — browser sets it with boundary for FormData

    const response = await fetch(`${EDGE_FUNCTIONS_URL}${EDGE_ENDPOINTS.UPLOAD.SCHEMA}`, {
      method: 'POST',
      headers,
      body: formData,
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Upload failed');
    }
    return response.json();
  },

  uploadFieldInstructions: async (formData: FormData) => {
    const token = localStorage.getItem('auth_token');
    const orgId = localStorage.getItem('active_org_id');
    const schema = localStorage.getItem('active_schema');
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (orgId) headers['X-Organization-Id'] = orgId;
    if (schema) headers['X-Schema'] = schema;

    const response = await fetch(`${EDGE_FUNCTIONS_URL}${EDGE_ENDPOINTS.UPLOAD.FIELD_INSTRUCTIONS}`, {
      method: 'POST',
      headers,
      body: formData,
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Upload failed');
    }
    return response.json();
  },

  // ─── Generic HTTP methods (through api Edge Function) ──────────
  get: async (url: string): Promise<any> => {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch data');
    }
    return response.json();
  },

  post: async (url: string, data?: any): Promise<any> => {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: data ? JSON.stringify(data) : undefined,
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to create data');
    }
    return response.json();
  },

  put: async (url: string, data?: any): Promise<any> => {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: data ? JSON.stringify(data) : undefined,
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update data');
    }
    return response.json();
  },

  patch: async (url: string, data?: any): Promise<any> => {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: data ? JSON.stringify(data) : undefined,
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update data');
    }
    return response.json();
  },

  delete: async (url: string): Promise<any> => {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to delete data');
    }
    return response.json();
  },
};
